import { FC, useEffect } from 'react';

import { differenceInDays, isValid } from 'date-fns';

import Analytics from '@hh.ru/analytics-js';
import { Button, HSpacingContainer } from '@hh.ru/magritte-ui';

import localStorageWrapper from 'bloko/common/storage/LocalStorageWrapper';

import useTrls from 'hooks/translation/useTrls';

import { DashboardBanner } from 'components/Dashboard/DashboardBanner/DashboardBanner';
import { WebinarBannerConfig } from 'components/Dashboard/WebinarBanner/webinarBannerConfig';

import { selectIsWebinarBannerVisible, setIsWebinarBannerVisible } from 'store/autoReducers';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    button: 'Dashboard.WebinarBanner.button',
    close: 'Dashboard.WebinarBanner.close',
};

const CURRENT_DATE = new Date();

export const WebinarBanner: FC<WebinarBannerConfig> = ({
    titleTrlKey,
    descriptionTrlKey,
    buttonLinkTrlKey,
    analyticsButtonClickKey,
    expirationDateTime,
    localStorageDisableOnClickKey,
    localStorageLastDateKey,
    showPeriodInDays,
}) => {
    const isVisible = useSelector(selectIsWebinarBannerVisible);
    const accountId = useSelector((state) => state.user.user?.currentAccount.id);
    const userId = useSelector((state) => state.user.user?.id);

    const trls = useTrls();
    const dispatch = useDispatch();

    useEffect(() => {
        const isWebinarBannerDisabled = localStorageDisableOnClickKey
            ? Boolean(localStorageWrapper.getItem(localStorageDisableOnClickKey))
            : false;

        if (isWebinarBannerDisabled) {
            dispatch(setIsWebinarBannerVisible(false));
            return;
        }

        if (CURRENT_DATE > expirationDateTime) {
            dispatch(setIsWebinarBannerVisible(false));
            return;
        }

        if (localStorageLastDateKey && showPeriodInDays) {
            let isShouldShowByPeriod = false;
            const lastShownDate = new Date(localStorageWrapper.getItem(localStorageLastDateKey) || '');

            if (isValid(lastShownDate)) {
                const daysSinceLastShown = differenceInDays(CURRENT_DATE, lastShownDate);
                isShouldShowByPeriod = daysSinceLastShown >= showPeriodInDays;
            } else {
                isShouldShowByPeriod = true;
            }

            dispatch(setIsWebinarBannerVisible(isShouldShowByPeriod));

            if (isShouldShowByPeriod) {
                localStorageWrapper.removeItem(localStorageLastDateKey);
            }
            return;
        }

        dispatch(setIsWebinarBannerVisible(true));
    }, [dispatch, expirationDateTime, localStorageDisableOnClickKey, localStorageLastDateKey, showPeriodInDays]);

    const onClose = () => {
        if (localStorageLastDateKey) {
            localStorageWrapper.setItem(localStorageLastDateKey, new Date().toISOString());
        }
        dispatch(setIsWebinarBannerVisible(false));
    };

    const onButtonClick = () => {
        Analytics.sendHHEventButtonClick(analyticsButtonClickKey, {
            accountId,
            userId,
        });
        if (localStorageDisableOnClickKey) {
            localStorageWrapper.setItem(localStorageDisableOnClickKey, 'true');
        }
    };

    if (!isVisible) {
        return null;
    }

    return (
        <DashboardBanner
            title={trls(titleTrlKey)}
            description={trls(descriptionTrlKey)}
            additionalContent={
                <HSpacingContainer default={12}>
                    {buttonLinkTrlKey && (
                        <Button
                            Element="a"
                            href={trls(buttonLinkTrlKey)}
                            target="_blank"
                            mode="primary"
                            style="accent"
                            onClick={onButtonClick}
                        >
                            {trls(TrlKeys.button)}
                        </Button>
                    )}
                    <Button mode="tertiary" style="accent" onClick={onClose}>
                        {trls(TrlKeys.close)}
                    </Button>
                </HSpacingContainer>
            }
            onClose={onClose}
        />
    );
};
